// Getting started → Imports — the data behind /getting-started/imports (POB
// S10, pages/gettingStartedImports.js): the import connectors, the owner's own
// upload, the preview, the column → field mapping onto an installed app's
// collection or one of the workspace's own collections, the run and its undo.
// The onboarding lane owns the server and these SDK names (POB plan §7b-3
// "Import core", the SDK's ImportsService):
//   listImportSources(wsId) → { sources: [{ id: 'file'|'google_sheets', kind,
//                               name { en, ka } | null, status live|planned|
//                               missing, auth }] } — the CONNECTORS. The `file`
//                               one is the drop zone; one that is not live
//                               says "Soon". (The files and pages the
//                               onboarding research finds on the company's own
//                               site are a later step, not a source here.)
//   listImportTargets(wsId) → [{ app (null = the workspace's own collection),
//                               collection, label{en,ka}, fields: [{ key,
//                               label{en,ka}, type, required }], open? }]
//                               (`open`: no fields declared, so every column
//                               becomes a field of its own)
//   uploadImportFile(wsId, file) → { fileId, name, size, kind } — 400
//                               `unsupported_file` (pdf/csv/xlsx only), 413
//                               `too_large` (10 MB). A PDF still uploads, but
//                               the page does not offer it until the server
//                               reads PDF tables.
//   previewImport(wsId, { fileId } | { sourceId }) → { previewId, columns:
//                               [{ key, label, sample, fieldKey? }] (fieldKey:
//                               the column's new field for an open target,
//                               the server's naming), rows (the first 5),
//                               total, suggestedTarget { app, collection } |
//                               null, suggestedMapping { column: field } (for
//                               an open target: the same keys), expiresAt }
//                               — a file by its fileId only (`{ sourceId:
//                               'file' }` is a 400); 404 `object_not_found` /
//                               `preview_not_found`
//                               (an upload lives 24 h), 413 `too_large` (5000
//                               rows or 10 MB), 422 `no_table_found` (every
//                               PDF today), 400 `unknown_connector`, 409
//                               `connector_not_available`
//   runImport(wsId, { previewId, target: { app, collection }, mapping })
//                             → 202 { importId } — 404 `preview_not_found`
//   getImport(wsId, importId) → { status running|done|failed|undone,
//                               counts { created, skipped, failed }, errors }
//                               — 404 `import_not_found`
//   undoImport(wsId, importId) — removes the rows of a done or a failed import
//                               (7 days); 409 `already_undone`
//
// Until the server answers — the SDK has no such method yet, the route
// answers 501, or a 404 with no code the contract knows — the page says
// "Soon" and offers no button (plan §7b-2: no dead buttons). A 403 is the
// manager gate.
//
// Root keys (state-app.js, the workspace-scoped wipe): the read
// (`gettingStartedImportStatus` idle|loading|ready|soon|denied|error,
// `gettingStartedSources`, `gettingStartedTargets`), the open review
// (`gettingStartedReview` { previewId, name, total, columns } — what the
// preview holds; null on the list), the owner's choice for it
// (`gettingStartedMapping` { targetKey, mapping }), the last refusal
// (`gettingStartedImportError`: a server code, or expired / pdf_not_read /
// connector_not_available / unavailable / failed), the busy flag while a file
// uploads or a preview reads (`gettingStartedUploading`) and the run
// (`gettingStartedRun`). The choice is its own key so a pick never replaces
// the review the column list renders from: the list keeps its rows and the
// select keeps focus. Every write carries the whole object: `mapping` holds
// EVERY column of the preview ('' = skip it), so no write can leave a stale
// column behind.
//
// Row fields are `source*` / `target*` / `field*` / `column*`-named on purpose:
// a `childrenAs: 'state'` row falls back to ROOT keys for a field it does not
// own, and the root has its own `id`, `label`, `kind` and `url`.
import _wsShared from '@symbo-ls/workspace-shared'

// Module helpers carry a `_gsi` prefix: frank hoists a functions file's
// module helpers into the project's one shared scope by name.
const _gsiRootOf = (root, ctx) => root || (ctx && ctx.state && ctx.state.root) || null
// The tab's own workspace beats the session claim (it lags a switch); either
// id shape — the scopedClient.js `activeWorkspaceId` rule.
const _gsiWsId = (r) =>
  String(
    (r && r.workspace && (r.workspace.id || r.workspace._id)) ||
      (r && r.jwtClaims && r.jwtClaims.active_workspace_id) ||
      ''
  )
const _gsiSdk = (seam, ctx) =>
  (seam && seam.sdk) || (ctx && ctx.sdk) || _wsShared.functions.getSDK()
const _gsiHas = (sdk, name) => !!sdk && typeof sdk[name] === 'function'
const _gsiSeam = (via) => (via && typeof via === 'object' ? via : {})
const _gsiWriter = (seam, r) =>
  typeof seam.write === 'function' ? seam.write : (patch) => r.update(patch)
// The SDK raises an HTTP failure with `status` and the JSON body as `cause`;
// the server names the reason in `error` (the SDK copies it to `code`).
const _gsiCode = (err) =>
  String((err && ((err.cause && (err.cause.error || err.cause.code)) || err.code)) || '')
// The contract's 404 codes: a thing that is gone, never a route not there yet.
const _gsiGone = (code) =>
  code === 'preview_not_found' || code === 'object_not_found' || code === 'import_not_found'
// The route is not there yet: 501, or a 404 with no code the contract knows.
const _gsiNotThere = (err) =>
  !!err && (err.status === 501 || (err.status === 404 && !_gsiGone(_gsiCode(err))))
// The reason a refusal shows: the server's own code when the page knows it,
// else the status the contract gives that code.
const _gsiErrorOf = (err) => {
  const code = _gsiCode(err)
  if (code === 'unsupported_file' || code === 'too_large' || code === 'no_table_found') return code
  // The upload (or its preview) is gone after 24 h: it goes up again.
  if (code === 'preview_not_found' || code === 'object_not_found') return 'expired'
  if (code === 'unknown_connector' || code === 'connector_not_available') {
    return 'connector_not_available'
  }
  if (err && err.status === 413) return 'too_large'
  if (err && err.status === 422) return 'no_table_found'
  if (_gsiNotThere(err)) return 'unavailable'
  return 'failed'
}
const _gsiErrorKey = (code) => {
  if (!code) return ''
  if (code === 'unsupported_file') return 'workspace.gettingStarted.imports.errUnsupported'
  if (code === 'too_large') return 'workspace.gettingStarted.imports.errTooLarge'
  if (code === 'no_table_found') return 'workspace.gettingStarted.imports.errNoTable'
  if (code === 'pdf_not_read') return 'workspace.gettingStarted.imports.errPdfNotRead'
  if (code === 'expired') return 'workspace.gettingStarted.imports.errExpired'
  if (code === 'connector_not_available') return 'workspace.gettingStarted.imports.errSourceSoon'
  if (code === 'unavailable') return 'workspace.gettingStarted.imports.errUnavailable'
  return 'workspace.gettingStarted.imports.errFailed'
}
// An answer may come wrapped in `{ data }`.
const _gsiBody = (res) =>
  (res && res.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res) ||
  {}
const _gsiText = (v) => (typeof v === 'string' ? v.trim() : '')
// A name in both languages: the server's { en, ka } (Georgian falls back to
// English), a plain string, else `fallback` (a connector's id, a collection's
// key) — never an empty name.
const _gsiLabelOf = (label, fallback) => {
  const obj = label && typeof label === 'object' ? label : null
  const en = obj ? _gsiText(obj.en) || _gsiText(obj.ka) : _gsiText(label)
  const text = en || String(fallback == null ? '' : fallback)
  return { en: text, ka: (obj && _gsiText(obj.ka)) || text }
}
const _gsiNameIn = (r, label) => label[(r && r.lang) === 'ka' ? 'ka' : 'en'] || label.en
const _gsiSource = (x) => {
  const id = String((x && (x.id || x._id)) || '')
  return {
    sourceId: id,
    sourceKind: String((x && x.kind) || id),
    sourceAuth: String((x && x.auth) || ''),
    sourceStatus: String((x && x.status) || ''),
    sourceName: _gsiLabelOf(x && x.name, id)
  }
}
const _gsiSourcesOf = (res) => {
  const body = _gsiBody(res)
  return (Array.isArray(body.sources) ? body.sources : []).map(_gsiSource).filter((x) => x.sourceId)
}
const _gsiSourceOf = (r, id) =>
  ((r && r.gettingStartedSources) || []).find((x) => x.sourceId === id) || null
const _gsiLive = (x) => !!x && x.sourceStatus === 'live'
// The file connector is the drop zone: a file previews by its fileId, never
// by `{ sourceId: 'file' }` (a 400).
const _gsiIsFile = (x) => !!x && x.sourceId === 'file'
// A connector that previews by `{ sourceId }`: a live one other than the file.
const _gsiPreviewable = (x) => _gsiLive(x) && !_gsiIsFile(x)
const _gsiTarget = (t) => {
  const app = t && t.app ? String(t.app) : null
  const collection = String((t && t.collection) || '')
  return {
    targetKey: `${app || ''}|${collection}`,
    // null: one of the workspace's own collections — the run names no app.
    targetApp: app,
    targetCollection: collection,
    targetLabel: _gsiLabelOf(t && t.label, collection),
    // No fields declared: every column becomes a field of its own.
    targetOpen: !!t && t.open === true,
    targetFields: (t && Array.isArray(t.fields) ? t.fields : [])
      .filter((f) => f && f.key)
      .map((f) => ({
        fieldKey: String(f.key),
        fieldLabel: _gsiLabelOf(f.label, f.key),
        fieldRequired: !!f.required
      }))
  }
}
const _gsiTargetsOf = (res) => {
  const body = Array.isArray(res) ? res : _gsiBody(res)
  const list = Array.isArray(body) ? body : Array.isArray(body.targets) ? body.targets : []
  return list.map(_gsiTarget).filter((t) => t.targetCollection)
}
const _gsiTargetOf = (r, key) =>
  ((r && r.gettingStartedTargets) || []).find((t) => t.targetKey === key) || null
// A field key the server takes for an open target (mapping.js FIELD_KEY_RE).
const _gsiFieldKeyOk = (v) => typeof v === 'string' && /^[a-zA-Z_][a-zA-Z0-9_]{0,63}$/.test(v)
// An open target's field per column. The server names them (Georgian
// headers transliterated) and the preview carries them (`columnField`): the
// server's key first, as given; a column with none gets `c<n>`, the server's
// own fallback, and a name already taken gets `_2`, `_3` … within 64
// characters.
const _gsiOwnKeys = (columns) => {
  const list = Array.isArray(columns) ? columns : []
  // A Set: a header may name `constructor`, which a plain object already has.
  const used = new Set()
  const out = {}
  const claim = (column, base) => {
    let key = base
    for (let n = 2; used.has(key); n++) key = `${base.slice(0, 64 - String(n).length - 1)}_${n}`
    used.add(key)
    out[column] = key
  }
  list.forEach((c) => {
    if (c.columnField) claim(c.columnKey, c.columnField)
  })
  list.forEach((c, i) => {
    if (!c.columnField) claim(c.columnKey, `c${i + 1}`)
  })
  return out
}
const _gsiColumnsOf = (r) => {
  const review = r && r.gettingStartedReview
  return (review && review.columns) || []
}
// The fields a column may go into: the target's own, or for an open target
// one new field per column.
const _gsiFieldsOf = (r, target) => {
  if (!target) return []
  if (!target.targetOpen) return target.targetFields
  const columns = _gsiColumnsOf(r)
  const own = _gsiOwnKeys(columns)
  return columns.map((c) => ({
    fieldKey: own[c.columnKey],
    fieldLabel: { en: c.columnLabel, ka: c.columnLabel },
    fieldRequired: false
  }))
}
// pdf / csv / xlsx by extension, or by MIME type when the name has none.
const _gsiKindOf = (file) => {
  const ext = String((file && file.name) || '')
    .toLowerCase()
    .split('.')
    .pop()
  if (ext === 'pdf' || ext === 'csv' || ext === 'xlsx') return ext
  const type = String((file && file.type) || '')
  if (type === 'application/pdf') return 'pdf'
  if (type === 'text/csv') return 'csv'
  if (type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'xlsx'
  return ''
}
// The contract's own limits, checked before any upload: 10 MB, pdf/csv/xlsx.
const _gsiFileCheck = (file) => {
  if (!file || typeof file !== 'object' || !_gsiKindOf(file)) return 'unsupported_file'
  if (Number(file.size) > 10 * 1024 * 1024) return 'too_large'
  return ''
}
// Up to three non-empty values per column, from the preview rows (objects by
// column key, or arrays by position), else the column's own sample; and the
// field key the server gives the column for an open target: its own
// `fieldKey` first, else the mapping the server suggested for one (`given`);
// '' = none.
const _gsiColumns = (columns, rows, given) =>
  (Array.isArray(columns) ? columns : []).map((c, i) => {
    const key = String(c && c.key != null ? c.key : i)
    const samples = (Array.isArray(rows) ? rows : [])
      .map((row) => (Array.isArray(row) ? row[i] : row && row[key]))
      .filter((v) => v != null && String(v).trim() !== '')
      .map((v) => String(v))
      .slice(0, 3)
    const sample = c && c.sample != null ? String(c.sample) : ''
    const own = c && c.fieldKey
    const field = _gsiFieldKeyOk(own) ? own : given && given[key]
    return {
      columnKey: key,
      columnLabel: String((c && (c.label || c.key)) || ''),
      columnSamples: samples.length ? samples : sample ? [sample] : [],
      columnField: _gsiFieldKeyOk(field) ? field : ''
    }
  })
// The mapping onto one target: every column present, '' = skip. An open
// target takes every column as its own new field. Else a wanted field the
// target lacks is dropped, a field takes one column (the first), and a
// column still free takes the field its own name names.
const _gsiMapFor = (columns, wanted, target) => {
  const out = {}
  if (target && target.targetOpen) {
    const own = _gsiOwnKeys(columns)
    columns.forEach((c) => {
      out[c.columnKey] = own[c.columnKey]
    })
    return out
  }
  const fields = (target && target.targetFields) || []
  const has = (key) => fields.some((f) => f.fieldKey === key)
  const used = new Set()
  columns.forEach((c) => {
    const f = wanted && wanted[c.columnKey]
    if (f && has(f) && !used.has(f)) {
      out[c.columnKey] = f
      used.add(f)
    } else {
      out[c.columnKey] = ''
    }
  })
  columns.forEach((c) => {
    if (out[c.columnKey]) return
    const name = c.columnLabel.trim().toLowerCase()
    const hit = fields.find(
      (f) =>
        !used.has(f.fieldKey) &&
        name &&
        (f.fieldKey.toLowerCase() === name ||
          f.fieldLabel.en.toLowerCase() === name ||
          f.fieldLabel.ka.toLowerCase() === name)
    )
    if (hit) {
      out[c.columnKey] = hit.fieldKey
      used.add(hit.fieldKey)
    }
  })
  return out
}
const _gsiChoice = (r) => (r && r.gettingStartedMapping) || null
const _gsiPicked = (choice) => Object.values((choice && choice.mapping) || {}).filter(Boolean)
// A required field of the chosen target that no column goes into yet.
const _gsiMissing = (r) => {
  const choice = _gsiChoice(r)
  const target = choice && _gsiTargetOf(r, choice.targetKey)
  const picked = _gsiPicked(choice)
  return (
    !!target && target.targetFields.some((f) => f.fieldRequired && !picked.includes(f.fieldKey))
  )
}
const _gsiCanRun = (r) => {
  const review = r && r.gettingStartedReview
  const choice = _gsiChoice(r)
  if (!review || !review.previewId || !choice) return false
  if (!_gsiTargetOf(r, choice.targetKey)) return false
  return _gsiPicked(choice).length > 0 && !_gsiMissing(r)
}
// Undo is offered while a done or a failed run added rows (a failed run's
// partial rows go too) and they are not undone yet.
const _gsiCanUndo = (run) =>
  !!run &&
  !!run.importId &&
  (run.status === 'done' || run.status === 'failed') &&
  Number(run.created) > 0 &&
  run.undo !== 'done'
// May this column take this field? '' (skip) always; else a field of the
// target — for an open target, the column's own new field and no other.
const _gsiTakes = (r, target, columnKey, field) => {
  if (!field) return true
  if (!target) return false
  if (target.targetOpen) return _gsiOwnKeys(_gsiColumnsOf(r))[columnKey] === field
  return target.targetFields.some((f) => f.fieldKey === field)
}
const _gsiSourceFrom = (href) => {
  const s = String(href || '')
  const q = s.indexOf('?')
  if (q < 0) return ''
  try {
    return new URLSearchParams(s.slice(q + 1).split('#')[0]).get('source') || ''
  } catch (e) {
    return ''
  }
}
// The preview of a file or a live connector → the open review. Resolves the
// review, or null (refused, or the workspace changed meanwhile). `kind` is
// the uploaded file's (pdf / csv / xlsx; '' for a connector).
const _gsiPreview = async (r, wsId, sdk, write, ref, name, kind) => {
  if (!_gsiHas(sdk, 'previewImport')) {
    write({ gettingStartedUploading: false, gettingStartedImportError: 'unavailable' })
    return null
  }
  write({ gettingStartedUploading: true, gettingStartedImportError: '' })
  try {
    const body = _gsiBody(await sdk.previewImport(wsId, ref))
    if (_gsiWsId(r) !== wsId) return null
    const s = body.suggestedTarget || {}
    const suggested = _gsiTargetOf(r, `${s.app || ''}|${s.collection || ''}`)
    // An open target the server suggests comes with its field keys.
    const given = suggested && suggested.targetOpen ? body.suggestedMapping : null
    const columns = _gsiColumns(body.columns, body.rows, given)
    const review = {
      previewId: String(body.previewId || ''),
      name: String(name || ''),
      total: Number(body.total) || 0,
      columns
    }
    write({
      gettingStartedUploading: false,
      gettingStartedImportError: '',
      gettingStartedReview: review,
      gettingStartedMapping: {
        targetKey: suggested ? suggested.targetKey : '',
        mapping: _gsiMapFor(columns, body.suggestedMapping || {}, suggested)
      },
      gettingStartedRun: null
    })
    return review
  } catch (err) {
    console.error('[getting-started/imports] preview', err)
    if (_gsiWsId(r) !== wsId) return null
    const code = _gsiErrorOf(err)
    // Every PDF answers no_table_found today: it says why.
    const shown = code === 'no_table_found' && kind === 'pdf' ? 'pdf_not_read' : code
    const patch = { gettingStartedUploading: false, gettingStartedImportError: shown }
    // The server cannot use this connector yet: its row says "Soon" too.
    if (code === 'connector_not_available' && ref && ref.sourceId) {
      patch.gettingStartedSources = (r.gettingStartedSources || []).map((x) =>
        x.sourceId === ref.sourceId ? { ...x, sourceStatus: 'planned' } : x
      )
    }
    write(patch)
    return null
  }
}

// el.call('loadGettingStartedImports', s.root, { sourceId }) — the page's
// onCreate (a `?source=` link opens that connector's review once the list
// lands), and the shell's switch tail (functions/loadPrivateData.js) while
// the page is on screen, with `{ sdk, write: put }` (its epoch-guarded
// writer).
export async function loadGettingStartedImports(root, via) {
  const r = _gsiRootOf(root, this)
  if (!r) return
  const seam = _gsiSeam(via)
  // A cold boot that settles on another workspace later re-runs the read
  // (the History loader's boot-identity rule).
  _wsShared.functions.onWorkspaceIdentityCorrected(loadGettingStartedImports)
  const wsId = _gsiWsId(r)
  if (!wsId) {
    _wsShared.functions.onActiveWorkspaceFull(loadGettingStartedImports)
    return
  }
  const write = _gsiWriter(seam, r)
  // A reply that outlived its workspace never lands.
  const land = (patch) => {
    if (_gsiWsId(r) === wsId) write(patch)
  }
  const sdk = _gsiSdk(seam, this)
  if (!_gsiHas(sdk, 'listImportSources')) {
    land({ gettingStartedImportStatus: 'soon' })
    return
  }
  land({ gettingStartedImportStatus: 'loading' })
  try {
    const [res, targets] = await Promise.all([
      sdk.listImportSources(wsId),
      // No targets route yet = nothing to import into, never a failed page.
      _gsiHas(sdk, 'listImportTargets') ? sdk.listImportTargets(wsId).catch(() => []) : []
    ])
    if (_gsiWsId(r) !== wsId) return
    const sources = _gsiSourcesOf(res)
    // No connector at all: nothing to import from yet, so "Soon" — never a
    // list with nothing on it.
    if (!sources.length) {
      land({ gettingStartedImportStatus: 'soon' })
      return
    }
    land({
      gettingStartedImportStatus: 'ready',
      gettingStartedSources: sources,
      gettingStartedTargets: _gsiTargetsOf(targets)
    })
  } catch (err) {
    if (_gsiNotThere(err)) {
      land({ gettingStartedImportStatus: 'soon' })
      return
    }
    if (err && err.status === 403) {
      land({ gettingStartedImportStatus: 'denied' })
      return
    }
    console.error('[getting-started/imports]', err)
    land({ gettingStartedImportStatus: 'error' })
    return
  }
  // Only a live connector other than the file previews by its id: the file,
  // a "Soon" connector and a research find (a later step) open nothing.
  const sourceId = typeof seam.sourceId === 'string' ? seam.sourceId : ''
  const source = sourceId ? _gsiSourceOf(r, sourceId) : null
  if (_gsiPreviewable(source)) {
    await _gsiPreview(r, wsId, sdk, write, { sourceId }, _gsiNameIn(r, source.sourceName))
  }
}

// The connectors the list renders as rows: every one but the live file (that
// one is the drop zone), in plain values — a row state holds no objects.
export function gettingStartedImportRows(root) {
  const r = _gsiRootOf(root, this)
  return ((r && r.gettingStartedSources) || [])
    .filter((x) => !(_gsiIsFile(x) && _gsiLive(x)))
    .map((x) => {
      const name = _gsiLabelOf(x.sourceName, x.sourceId)
      return {
        sourceId: x.sourceId,
        sourceNameEn: name.en,
        sourceNameKa: name.ka,
        sourceLive: _gsiLive(x)
      }
    })
}

// The drop zone shows while the file connector is live.
export function gettingStartedImportCanUpload(root) {
  const r = _gsiRootOf(root, this)
  return ((r && r.gettingStartedSources) || []).some((x) => _gsiIsFile(x) && _gsiLive(x))
}

// '' when the file may go up; else the reason it may not (`unsupported_file`
// / `too_large`), the same codes the server answers.
export function gettingStartedFileCheck(file) {
  return _gsiFileCheck(file)
}

// The owner's own file: checked here, uploaded, then previewed by its fileId.
export async function uploadGettingStartedFile(root, file, via) {
  const r = _gsiRootOf(root, this)
  if (!r || r.gettingStartedUploading) return null
  const seam = _gsiSeam(via)
  const wsId = _gsiWsId(r)
  if (!wsId) return null
  const write = _gsiWriter(seam, r)
  const refused = _gsiFileCheck(file)
  if (refused) {
    write({ gettingStartedImportError: refused })
    return null
  }
  const sdk = _gsiSdk(seam, this)
  if (!_gsiHas(sdk, 'uploadImportFile')) {
    write({ gettingStartedImportError: 'unavailable' })
    return null
  }
  write({ gettingStartedUploading: true, gettingStartedImportError: '' })
  let up
  try {
    up = _gsiBody(await sdk.uploadImportFile(wsId, file))
  } catch (err) {
    console.error('[getting-started/imports] upload', err)
    if (_gsiWsId(r) === wsId) {
      write({ gettingStartedUploading: false, gettingStartedImportError: _gsiErrorOf(err) })
    }
    return null
  }
  if (_gsiWsId(r) !== wsId) return null
  if (!up.fileId) {
    write({ gettingStartedUploading: false, gettingStartedImportError: 'failed' })
    return null
  }
  return _gsiPreview(
    r,
    wsId,
    sdk,
    write,
    { fileId: up.fileId },
    up.name || file.name,
    String(up.kind || _gsiKindOf(file))
  )
}

// "Preview" on a live connector other than the file.
export async function reviewGettingStartedSource(root, sourceId, via) {
  const r = _gsiRootOf(root, this)
  if (!r || !sourceId || r.gettingStartedUploading) return null
  const source = _gsiSourceOf(r, sourceId)
  const wsId = _gsiWsId(r)
  if (!_gsiPreviewable(source) || !wsId) return null
  const seam = _gsiSeam(via)
  return _gsiPreview(
    r,
    wsId,
    _gsiSdk(seam, this),
    _gsiWriter(seam, r),
    { sourceId },
    _gsiNameIn(r, source.sourceName)
  )
}

// Back to the list. A run still going keeps going on the server.
export function closeGettingStartedReview(root) {
  const r = _gsiRootOf(root, this)
  if (!r) return
  r.update({
    gettingStartedReview: null,
    gettingStartedMapping: null,
    gettingStartedRun: null,
    gettingStartedImportError: ''
  })
}

// The columns of the open review — the rows the page lists, one select each.
// The samples arrive as one line of text: a row state holds plain values.
export function gettingStartedImportColumns(root) {
  const r = _gsiRootOf(root, this)
  return _gsiColumnsOf(r).map((c) => ({
    columnKey: c.columnKey,
    columnLabel: c.columnLabel,
    columnSampleText: (c.columnSamples || []).join(', ')
  }))
}

// "Import into": another target. The columns keep the fields the new target
// has; an open target takes every column as a new field.
export function setGettingStartedImportTarget(root, targetKey) {
  const r = _gsiRootOf(root, this)
  const review = r && r.gettingStartedReview
  const target = _gsiTargetOf(r, targetKey)
  if (!review || !target) return
  const choice = _gsiChoice(r) || {}
  r.update({
    gettingStartedMapping: {
      targetKey: target.targetKey,
      mapping: _gsiMapFor(review.columns || [], choice.mapping || {}, target)
    }
  })
}

// One column's pick: a field of the target, or '' to skip the column. A field
// takes one column — picking it here frees the column that had it.
export function setGettingStartedImportMapping(root, columnKey, fieldKey) {
  const r = _gsiRootOf(root, this)
  const choice = _gsiChoice(r)
  if (
    !choice ||
    !choice.mapping ||
    !Object.prototype.hasOwnProperty.call(choice.mapping, columnKey)
  ) {
    return
  }
  const field = String(fieldKey || '')
  if (!_gsiTakes(r, _gsiTargetOf(r, choice.targetKey), columnKey, field)) return
  const mapping = {}
  Object.keys(choice.mapping).forEach((col) => {
    if (col === columnKey) mapping[col] = field
    else mapping[col] = field && choice.mapping[col] === field ? '' : choice.mapping[col]
  })
  r.update({ gettingStartedMapping: { targetKey: choice.targetKey, mapping } })
}

// One column's current pick ('' = skipped) — each row reads its own, so a
// pick re-runs that row's props and never rebuilds the list.
export function gettingStartedImportPick(root, columnKey) {
  const r = _gsiRootOf(root, this)
  const choice = _gsiChoice(r)
  return String((choice && choice.mapping && choice.mapping[columnKey]) || '')
}

// The fields of the chosen target (an open one's: one new field per column).
export function gettingStartedImportFields(root) {
  const r = _gsiRootOf(root, this)
  const choice = _gsiChoice(r)
  const target = choice && _gsiTargetOf(r, choice.targetKey)
  return _gsiFieldsOf(r, target).map((f) => ({ ...f, fieldLabel: { ...f.fieldLabel } }))
}

// The <option> rows of the "Import into" select, labels in the page language.
export function gettingStartedImportTargetOptions(root, pickText) {
  const r = _gsiRootOf(root, this)
  const choice = _gsiChoice(r)
  const current = (choice && choice.targetKey) || ''
  const rows = ((r && r.gettingStartedTargets) || []).map((t) => ({
    value: t.targetKey,
    text: _gsiNameIn(r, t.targetLabel),
    selected: t.targetKey === current ? true : undefined
  }))
  return current ? rows : [{ value: '', text: String(pickText || ''), selected: true }, ...rows]
}

// The <option> rows of one column's field select: "Skip this column", then
// the target's fields ("(required)" after a required one) — for an open
// target, "Add as a new field" (the column's own new field).
export function gettingStartedImportFieldOptions(root, columnKey, words) {
  const r = _gsiRootOf(root, this)
  const lang = (r && r.lang) === 'ka' ? 'ka' : 'en'
  const choice = _gsiChoice(r)
  const target = choice && _gsiTargetOf(r, choice.targetKey)
  const pick = String((choice && choice.mapping && choice.mapping[columnKey]) || '')
  const w = words && typeof words === 'object' ? words : {}
  const skip = { value: '', text: String(w.skip || ''), selected: pick ? undefined : true }
  if (target && target.targetOpen) {
    const own = _gsiOwnKeys(_gsiColumnsOf(r))[columnKey]
    const add = {
      value: own,
      text: String(w.newField || ''),
      selected: pick === own ? true : undefined
    }
    return own ? [skip, add] : [skip]
  }
  const fields = (target ? target.targetFields : []).map((f) => ({
    value: f.fieldKey,
    text: f.fieldRequired
      ? `${f.fieldLabel[lang] || f.fieldLabel.en} ${String(w.required || '')}`.trim()
      : f.fieldLabel[lang] || f.fieldLabel.en,
    selected: f.fieldKey === pick ? true : undefined
  }))
  return [skip, ...fields]
}

// "Import rows (N)" may run: a target, a mapped column and every required field.
export function gettingStartedImportCanRun(root) {
  return _gsiCanRun(_gsiRootOf(root, this))
}

// The bundle key of a refusal code ('' when there is none).
export function gettingStartedImportErrorKey(code) {
  return _gsiErrorKey(code)
}

// The key of the line under the review: the refusal, the run's outcome, the
// undo's, or why "Import" cannot run yet ('' = nothing to say).
export function gettingStartedImportNoteKey(root) {
  const r = _gsiRootOf(root, this)
  const code = r && r.gettingStartedImportError
  if (code) return _gsiErrorKey(code)
  const run = r && r.gettingStartedRun
  if (run) {
    // The undo's outcome first: a failed run's rows may be undone too.
    if (run.undo === 'done') return 'workspace.gettingStarted.imports.undone'
    if (run.undo === 'failed') return 'workspace.gettingStarted.imports.undoFailed'
    if (run.status === 'failed') return 'workspace.gettingStarted.imports.runFailed'
    if (run.status === 'pending') return 'workspace.gettingStarted.imports.pending'
    if (run.status !== 'done') return ''
    return 'workspace.gettingStarted.imports.done'
  }
  const choice = _gsiChoice(r)
  if (!choice || !choice.targetKey || _gsiCanRun(r)) return ''
  if (_gsiMissing(r)) return 'workspace.gettingStarted.imports.needRequired'
  return _gsiPicked(choice).length ? '' : 'workspace.gettingStarted.imports.needColumn'
}

// "Import N rows": the run from the preview, then a poll every 2 s for up to
// 5 minutes. A run still going after that is left to the server ("pending").
// Resolves { importId, status }, or null (refused, nothing to run, or the
// review / the workspace changed meanwhile).
export async function runGettingStartedImport(root, via) {
  const r = _gsiRootOf(root, this)
  if (!r || !_gsiCanRun(r)) return null
  // One thing at a time: a run going, or the undo of the last one (its rows
  // are still going; a new run would race it for the run on screen).
  const last = r.gettingStartedRun
  if (
    last &&
    (last.status === 'starting' || last.status === 'running' || last.undo === 'undoing')
  ) {
    return null
  }
  const seam = _gsiSeam(via)
  const wsId = _gsiWsId(r)
  if (!wsId) return null
  const write = _gsiWriter(seam, r)
  const sdk = _gsiSdk(seam, this)
  if (!_gsiHas(sdk, 'runImport') || !_gsiHas(sdk, 'getImport')) {
    write({ gettingStartedImportError: 'unavailable' })
    return null
  }
  const sleep =
    typeof seam.sleep === 'function'
      ? seam.sleep
      : (ms) => new Promise((res) => setTimeout(res, ms))
  const maxPolls = Number(seam.maxPolls) > 0 ? Number(seam.maxPolls) : 150
  const review = r.gettingStartedReview
  const choice = _gsiChoice(r)
  const target = _gsiTargetOf(r, choice.targetKey)
  const mapping = {}
  Object.keys(choice.mapping || {}).forEach((col) => {
    if (choice.mapping[col]) mapping[col] = choice.mapping[col]
  })
  const fresh = (importId, status, counts) => ({
    importId,
    status,
    created: Number(counts && counts.created) || 0,
    skipped: Number(counts && counts.skipped) || 0,
    failed: Number(counts && counts.failed) || 0,
    undo: ''
  })
  write({ gettingStartedImportError: '', gettingStartedRun: fresh('', 'starting', null) })
  let importId = ''
  try {
    const body = _gsiBody(
      await sdk.runImport(wsId, {
        previewId: review.previewId,
        target: { app: target.targetApp, collection: target.targetCollection },
        mapping
      })
    )
    importId = String(body.importId || body.id || '')
  } catch (err) {
    console.error('[getting-started/imports] run', err)
    if (_gsiWsId(r) !== wsId) return null
    const code = _gsiErrorOf(err)
    // The preview is gone (24 h, or a finished run used it up): back to the
    // list, where the file goes up again.
    const gone =
      code === 'expired' ? { gettingStartedReview: null, gettingStartedMapping: null } : {}
    write({ ...gone, gettingStartedRun: null, gettingStartedImportError: code })
    return null
  }
  if (_gsiWsId(r) !== wsId) return null
  if (!importId) {
    write({ gettingStartedRun: null, gettingStartedImportError: 'failed' })
    return null
  }
  let run = fresh(importId, 'running', null)
  write({ gettingStartedRun: run })
  for (let i = 0; i < maxPolls; i++) {
    await sleep(2000)
    // The page went back to the list, another run began, or the workspace
    // changed: this poll is no longer anyone's.
    const mine = () =>
      _gsiWsId(r) === wsId && r.gettingStartedRun && r.gettingStartedRun.importId === importId
    if (!mine()) return null
    let st = null
    let lost = false
    try {
      st = _gsiBody(await sdk.getImport(wsId, importId))
    } catch (err) {
      console.warn('[getting-started/imports] status read failed', err)
      // The server no longer knows this run; any other failed read is
      // retried by the next poll.
      lost = _gsiCode(err) === 'import_not_found'
    }
    if (!mine()) return null
    if (lost) {
      write({ gettingStartedRun: { ...run, status: 'failed' } })
      return { importId, status: 'failed' }
    }
    if (!st) continue
    // Undone meanwhile (another tab): a done run whose undo is done — the
    // note says so and Undo hides.
    if (st.status === 'undone') {
      write({ gettingStartedRun: { ...fresh(importId, 'done', st.counts), undo: 'done' } })
      return { importId, status: 'undone' }
    }
    const status = st.status === 'done' || st.status === 'failed' ? st.status : 'running'
    run = fresh(importId, status, st.counts)
    write({ gettingStartedRun: run })
    if (status !== 'running') return { importId, status }
  }
  write({ gettingStartedRun: { ...run, status: 'pending' } })
  return { importId, status: 'pending' }
}

// "Undo" shows while the run's rows may be removed.
export function gettingStartedImportCanUndo(root) {
  const r = _gsiRootOf(root, this)
  return _gsiCanUndo(r && r.gettingStartedRun)
}

// "Undo": the server removes the rows of that import (for 7 days) — a done
// run's, or the partial rows of a failed one.
export async function undoGettingStartedImport(root, via) {
  const r = _gsiRootOf(root, this)
  const run = r && r.gettingStartedRun
  if (!_gsiCanUndo(run) || run.undo === 'undoing') return false
  const seam = _gsiSeam(via)
  const wsId = _gsiWsId(r)
  if (!wsId) return false
  const write = _gsiWriter(seam, r)
  const sdk = _gsiSdk(seam, this)
  if (!_gsiHas(sdk, 'undoImport')) {
    write({ gettingStartedImportError: 'unavailable' })
    return false
  }
  write({ gettingStartedRun: { ...run, undo: 'undoing' } })
  // The outcome lands only while this run is still the one on screen (Back
  // or a workspace switch clears it meanwhile).
  const mine = () =>
    _gsiWsId(r) === wsId && !!r.gettingStartedRun && r.gettingStartedRun.importId === run.importId
  try {
    await sdk.undoImport(wsId, run.importId)
  } catch (err) {
    // Undone already (another tab): its rows are gone all the same.
    if (_gsiCode(err) !== 'already_undone') {
      console.error('[getting-started/imports] undo', err)
      if (mine()) write({ gettingStartedRun: { ...run, undo: 'failed' } })
      return false
    }
  }
  if (_gsiWsId(r) !== wsId) return false
  if (mine()) write({ gettingStartedRun: { ...run, undo: 'done' } })
  return true
}

// `?source=<id>` of an href ('' when it has none).
export function gettingStartedImportSourceFrom(href) {
  return _gsiSourceFrom(href)
}

// el.call('gettingStartedImportSourceParam') — the `?source=` this page was
// opened with: from its pane's href inside a PageSlot pane, else from the URL
// query at the dedicated URL (the router's `query`).
export function gettingStartedImportSourceParam() {
  const el = this
  const r = el && el.state && el.state.root
  const paneKey = el && typeof el.call === 'function' ? el.call('slotPaneKeyForStrict', el) : null
  if (paneKey) return _gsiSourceFrom(r && r[paneKey])
  const q = r && r.query
  return q && typeof q === 'object' && q.source ? String(q.source) : ''
}
